class BrowserSecurityService {
  constructor() {
    this.isLoggedOut = false;
    this.sessionToken = null;
    this.initializeBrowserSecurity();
  }

  initializeBrowserSecurity() {
    // Don't initialize on login page to prevent flickering
    if (window.location.pathname === '/login') {
      return;
    }
    
    // Prevent back button after logout
    this.preventBackNavigation();
    
    // Handle page refresh/reload
    this.handlePageReload();
    
    // Monitor browser navigation
    this.monitorNavigation();
    
    // Clear sensitive data on window close
    this.handleWindowClose();
  }

  preventBackNavigation() {
    window.history.pushState(null, null, window.location.href);
    
    window.addEventListener('popstate', (event) => {
      // Don't block navigation during any login flow (SSO/B2C)
      if (window.location.pathname === '/login' || 
          window.location.pathname === '/b2c-success' ||
          window.location.search.includes('code=') ||
          window.location.search.includes('state=') ||
          window.location.search.includes('error=') ||
          sessionStorage.getItem('b2c_auth_code') ||
          !this.isLoggedOut) {
        return;
      }
      
      // Only block if explicitly logged out and session invalid
      if (this.isLoggedOut && !this.isValidSession()) {
        window.history.pushState(null, null, window.location.href);
        this.redirectToLogin();
        return;
      }
    });
  }

  handlePageReload() {
    window.addEventListener('beforeunload', (event) => {
      if (this.isLoggedOut) {
        this.clearSessionData();
        return;
      }
      
      // Mark page as being reloaded
      sessionStorage.setItem('pageReloading', 'true');
      sessionStorage.setItem('lastActivity', Date.now().toString());
    });

    window.addEventListener('load', () => {
      const isReloading = sessionStorage.getItem('pageReloading');
      const lastActivity = sessionStorage.getItem('lastActivity');
      
      if (isReloading) {
        sessionStorage.removeItem('pageReloading');
        
        // Don't validate session immediately after login to prevent flickering
        if (window.location.pathname === '/login') {
          return;
        }
        
        // Check if session is still valid
        if (!this.isValidSession()) {
          this.handleUnauthorizedAccess();
          return;
        }
        
        // Increase session timeout to 1 hour to prevent frequent refreshes
        if (lastActivity && (Date.now() - parseInt(lastActivity)) > 3600000) {
          this.handleSessionTimeout();
          return;
        }
      }
    });
  }

  monitorNavigation() {
    // Monitor hash changes
    window.addEventListener('hashchange', () => {
      // Don't block during any login flow (SSO/B2C)
      if (window.location.pathname === '/login' ||
          window.location.pathname === '/b2c-success' ||
          window.location.search.includes('code=') ||
          window.location.search.includes('state=') ||
          window.location.search.includes('error=') ||
          sessionStorage.getItem('b2c_auth_code')) {
        return;
      }
      
      // Only check if explicitly logged out
      if (this.isLoggedOut && !this.isValidSession()) {
        this.handleUnauthorizedAccess();
      }
    });

    // Monitor navigation attempts
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      // Don't block navigation during any login flow (SSO/B2C)
      const url = args[2];
      if (url && (url.includes('/login') || url === '/login' || 
                 url.includes('/b2c-success') || url === '/b2c-success' ||
                 url.includes('code=') || url.includes('state=') ||
                 url.includes('error=')) || sessionStorage.getItem('b2c_auth_code')) {
        return originalPushState.apply(window.history, args);
      }
      
      // Only check if explicitly logged out
      if (this.isLoggedOut && !this.isValidSession()) {
        this.handleUnauthorizedAccess();
        return;
      }
      return originalPushState.apply(window.history, args);
    };

    window.history.replaceState = (...args) => {
      // Don't block navigation during any login flow (SSO/B2C)
      const url = args[2];
      if (url && (url.includes('/login') || url === '/login' ||
                 url.includes('/b2c-success') || url === '/b2c-success' ||
                 url.includes('code=') || url.includes('state=') ||
                 url.includes('error=')) || sessionStorage.getItem('b2c_auth_code')) {
        return originalReplaceState.apply(window.history, args);
      }
      
      // Only check if explicitly logged out
      if (this.isLoggedOut && !this.isValidSession()) {
        this.handleUnauthorizedAccess();
        return;
      }
      return originalReplaceState.apply(window.history, args);
    };
  }

  handleWindowClose() {
    window.addEventListener('beforeunload', () => {
      if (this.isLoggedOut) {
        this.clearAllSessionData();
      }
    });
  }

  setLogoutState() {
    this.isLoggedOut = true;
    this.sessionToken = null;
    
    // Clear browser cache
    this.clearBrowserCache();
    
    // Prevent back navigation
    window.history.pushState(null, null, '/login');
    
    // Clear session data
    this.clearSessionData();
  }

  isValidSession() {
    // Always allow access during login flows and B2C callbacks
    if (window.location.pathname === '/login' || 
        window.location.pathname === '/b2c-success' ||
        window.location.search.includes('code=') ||
        window.location.search.includes('state=') ||
        window.location.search.includes('error=') ||
        sessionStorage.getItem('b2c_auth_code')) {
      return true;
    }
    
    // Don't validate session if user is not logged in yet (during login flow)
    const userEmail = sessionStorage.getItem('userEmail');
    const userToken = sessionStorage.getItem('userToken');
    const userType = sessionStorage.getItem('user_type');
    
    if (!userEmail && !userToken && !userType) {
      // No session yet - this is normal during login flow
      return true;
    }
    
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const user = localStorage.getItem('user') || sessionStorage.getItem('user');
    
    if (!token || !user) {
      return false;
    }

    try {
      // Check token expiry (if JWT)
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp < Date.now() / 1000) {
        return false;
      }
    } catch (e) {
      // If not JWT, check timestamp
      const loginTime = localStorage.getItem('loginTime');
      if (loginTime && (Date.now() - parseInt(loginTime)) > 3600000) { // 1 hour
        return false;
      }
    }

    return true;
  }

  handleUnauthorizedAccess() {
    // Don't show unauthorized popup during any login flow
    if (window.location.pathname === '/login' ||
        window.location.pathname === '/b2c-success' ||
        window.location.search.includes('code=') ||
        window.location.search.includes('state=') ||
        window.location.search.includes('error=') ||
        sessionStorage.getItem('b2c_auth_code')) {
      return;
    }
    
    this.clearSessionData();
    this.showUnauthorizedMessage();
    this.redirectToLogin();
  }

  handleSessionTimeout() {
    // Don't show timeout popup during any login flow
    if (window.location.pathname === '/login' ||
        window.location.pathname === '/b2c-success' ||
        window.location.search.includes('code=') ||
        window.location.search.includes('state=') ||
        window.location.search.includes('error=') ||
        sessionStorage.getItem('b2c_auth_code')) {
      return;
    }
    
    this.clearSessionData();
    this.showTimeoutMessage();
    this.redirectToLogin();
  }

  clearBrowserCache() {
    // Clear cache headers
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Clear page cache
    window.location.replace = window.location.href;
  }

  clearSessionData() {
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear cookies
    document.cookie.split(";").forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  }

  clearAllSessionData() {
    this.clearSessionData();
    
    // Force page reload to clear memory
    if (!this.isLoggedOut) {
      window.location.reload(true);
    }
  }

  showUnauthorizedMessage() {
    const message = document.createElement('div');
    message.innerHTML = `
      <div style="
        position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
        background: rgba(0,0,0,0.8); z-index: 10000; display: flex; 
        align-items: center; justify-content: center; color: white;
        font-family: Arial, sans-serif;
      ">
        <div style="
          background: #fff; color: #333; padding: 30px; border-radius: 8px;
          text-align: center; max-width: 400px;
        ">
          <h3 style="color: #d32f2f; margin: 0 0 15px 0;">Unauthorized Access</h3>
          <p>You are not authorized to access this page. Please login again.</p>
          <button onclick="window.location.href='/login'" style="
            background: #1976d2; color: white; border: none; padding: 10px 20px;
            border-radius: 4px; cursor: pointer; margin-top: 10px;
          ">Go to Login</button>
        </div>
      </div>
    `;
    document.body.appendChild(message);

    setTimeout(() => {
      this.redirectToLogin();
    }, 3000);
  }

  showTimeoutMessage() {
    const message = document.createElement('div');
    message.innerHTML = `
      <div style="
        position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
        background: rgba(0,0,0,0.8); z-index: 10000; display: flex; 
        align-items: center; justify-content: center; color: white;
        font-family: Arial, sans-serif;
      ">
        <div style="
          background: #fff; color: #333; padding: 30px; border-radius: 8px;
          text-align: center; max-width: 400px;
        ">
          <h3 style="color: #f57c00; margin: 0 0 15px 0;">Session Timeout</h3>
          <p>Your session has expired. Please login again to continue.</p>
          <button onclick="window.location.href='/login'" style="
            background: #1976d2; color: white; border: none; padding: 10px 20px;
            border-radius: 4px; cursor: pointer; margin-top: 10px;
          ">Login Again</button>
        </div>
      </div>
    `;
    document.body.appendChild(message);

    setTimeout(() => {
      this.redirectToLogin();
    }, 3000);
  }

  redirectToLogin() {
    window.location.replace('/login');
  }

  // Initialize on app start
  static initialize() {
    // Don't initialize browser security during any login flow (SSO/B2C)
    if (window.location.pathname === '/login' ||
        window.location.pathname === '/b2c-success' ||
        window.location.search.includes('code=') ||
        window.location.search.includes('state=') ||
        window.location.search.includes('error=') ||
        sessionStorage.getItem('b2c_auth_code')) {
      return null;
    }
    
    if (!window.browserSecurityService) {
      window.browserSecurityService = new BrowserSecurityService();
    }
    return window.browserSecurityService;
  }

  // Call on logout
  static handleLogout() {
    if (window.browserSecurityService) {
      window.browserSecurityService.setLogoutState();
    }
  }
}

export default BrowserSecurityService;